<script setup lang="ts">
import { computed } from 'vue'
import { useExtendedI18n } from '@/i18n'
import type { Money } from '@/modules/shared/utils/money'
import type { File } from '@/modules/filing/stores/filing-store'
import { VCard, VCardTitle, VDescriptionList, ActionsMenu, ActionsGroup, ActionItem } from '@/modules/shared/components'
import LifecycleDateBanner from '@/modules/investing/components/lifecycle-date-banner.vue'

const props = defineProps<{
  entityType: 'spv' | 'fund'
  name: string
  url?: string
  description?: string
  sectors?: string[] | null
  stage?: string | null
  equityType?: string | null
  preMoneyValuation?: Money | null
  targetRaise?: Money | null
  minimumInvestment?: Money | null
  expectedCloseDate?: string
  lifecycleState?: 'raising' | 'closed' | 'exited'
  managementFee?: string
  carriedInterest?: string
  preferredReturn?: string
  hasStructure?: boolean
  files?: File[]
}>()

const { n } = useExtendedI18n()

const EMPTY = '—'

const sectorsLabel = computed(() => {
  const v = props.sectors
  return Array.isArray(v) && v.length ? v.join(', ') : EMPTY
})

const display = (value: unknown) => (value == null || value === '' ? EMPTY : (value as string))

const dealItems = computed(() => [
  { term: 'Sector', description: sectorsLabel.value },
  { term: 'Stage', description: display(props.stage) },
  { term: props.entityType === 'fund' ? 'Fund Type' : 'Type of Investment', description: display(props.equityType) },
  {
    term: props.entityType === 'fund' ? 'Minimum to Close' : 'Pre-Money Valuation',
    description: props.preMoneyValuation ? ((n(props.preMoneyValuation, 'currency') as string) ?? EMPTY) : EMPTY,
  },
  {
    term: 'Total Raise',
    description: props.targetRaise ? ((n(props.targetRaise, 'currency') as string) ?? EMPTY) : EMPTY,
  },
  {
    term: props.entityType === 'fund' ? 'Minimum Commitment' : 'Minimum Investment',
    description: props.minimumInvestment ? ((n(props.minimumInvestment, 'currency') as string) ?? EMPTY) : EMPTY,
  },
])

const feeItems = computed(() =>
  props.hasStructure
    ? [
        { term: 'Management Fee', description: props.managementFee ?? EMPTY },
        { term: 'Carried Interest', description: props.carriedInterest ?? EMPTY },
        { term: 'Preferred Return', description: props.preferredReturn ?? EMPTY },
      ]
    : [],
)

const openFile = (url: string) => window.open(url, '_blank')

const downloadFile = (url: string, name: string) => {
  const a = document.createElement('a')
  a.href = url
  a.download = name
  a.click()
}
</script>

<template>
  <VCard>
    <VCardTitle title="Investment Summary" />

    <div class="mb-1 text-lg font-bold text-gray-900">{{ name }}</div>
    <a
      v-if="url"
      :href="url.startsWith('http') ? url : `https://${url}`"
      target="_blank"
      rel="noopener"
      class="text-sm text-[#3b88af] hover:underline"
    >
      {{ url }}
    </a>

    <p v-if="description" class="mt-3 text-sm leading-relaxed text-gray-600">
      {{ description }}
    </p>

    <LifecycleDateBanner
      v-if="expectedCloseDate || lifecycleState === 'closed' || lifecycleState === 'exited'"
      :variant="lifecycleState ?? 'raising'"
      :date="expectedCloseDate || '—'"
    />

    <VDescriptionList class="mt-4" :items="dealItems" />

    <template v-if="feeItems.length">
      <div class="mt-4 border-t border-[var(--divider-color,#EEEFF0)] pt-4">
        <div class="text-xs font-semibold uppercase tracking-wider text-gray-500">Fees &amp; Economics</div>
        <VDescriptionList class="mt-2" :items="feeItems" />
      </div>
    </template>

    <div class="mt-4 border-t border-[var(--divider-color,#EEEFF0)] pt-4">
      <div class="text-xs font-semibold uppercase tracking-wider text-gray-500">Files</div>
      <div v-if="files && files.length" class="mt-2 space-y-1">
        <div v-for="file in files" :key="file.id" class="flex items-center justify-between rounded-md py-1.5">
          <div class="flex items-center gap-2">
            <div
              class="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded bg-red-50 text-[7px] font-extrabold text-red-600"
            >
              PDF
            </div>
            <a class="cursor-pointer text-sm text-gray-700" @click="openFile(file.file_url)">
              {{ file.name }}
            </a>
          </div>
          <div class="flex items-center gap-2">
            <ActionsMenu @click.stop>
              <ActionsGroup>
                <ActionItem text="View" @click="openFile(file.file_url)" />
                <ActionItem text="Download" @click="downloadFile(file.file_url, file.name)" />
              </ActionsGroup>
            </ActionsMenu>
          </div>
        </div>
      </div>
      <p v-else class="mt-2 text-sm text-gray-500">No supporting documents uploaded.</p>
    </div>
  </VCard>
</template>
